/**
 * Event Registry System
 *
 * This file contains all event definitions for the tracking system.
 * To add a new event:
 * 1. Add an event name constant below
 * 2. Define the event data interface
 * 3. Add an entry to the EventRegistry type
 *
 * Excluded events (not implemented here):
 * - Backend-only events (require server-side implementation):
 *     added-payment-method, completed-order, abandoned-order,
 *     completed-first-order, created-custom-package, added-user,
 *     downloaded-report, created-group, added-billing-profile, paying_user
 * - Tracked by Pendo view events (no custom implementation needed):
 *     viewed-payment-screen, logged-in, viewed-first-case,
 *     visited-integrations-page
 * - Already live: cp_initiated_order (Live - Feb 19)
 * - Derived calculation: time_to_first_order
 */

// Event name constants
export const EVENT_NAMES = {
  CP_INITIATED_ORDER: "CP_INITIATED_ORDER",
  CP_SUBMITTED_PAYMENT: "CP_SUBMITTED_PAYMENT",
  CP_SELECTED_PACKAGE: "CP_SELECTED_PACKAGE",
  CP_NEW_PACKAGE: "CP_NEW_PACKAGE",
  CP_BUNDLED_DEAL: "CP_BUNDLED_DEAL",
  CP_CHECK_TYPE: "CP_CHECK_TYPE",
  CP_UPLOAD_LOGO: "CP_UPLOAD_LOGO",
  CP_UPLOAD_ICON: "CP_UPLOAD_ICON",
  CP_ADDED_INTEGRATION: "CP_ADDED_INTEGRATION",
  CP_SETUP_RESCREEN: "CP_SETUP_RESCREEN",
} as const;

// Event data interfaces

export interface CpInitiatedOrderData {
  orderType: "package" | "custom";
}

export interface SubmittedPaymentData {
  paymentMethod?: string;
  orderValue?: string;
  checksOrdered?: string;
}

export interface SelectedPackageData {
  packageName: string;
  packagePrice?: string;
}

export interface NewPackageData {
  orderType: string;
}

export interface BundledDealData {
  bundledDeal: string;
}

export interface CheckTypeData {
  checkType: string;
  action: "add" | "remove";
}

export interface UploadLogoData {
  uploadLogo: boolean;
}

export interface UploadIconData {
  uploadIcon: boolean;
}

export interface AddedIntegrationData {
  sourcePage?: string;
  integrationName: string;
  authenticationMethod?: string;
  isFirstIntegration?: boolean;
}

export interface SetupRescreenData {
  rescreenFrequency: string;
  sourcePage?: string;
  checkTypes?: string;
}

// Event registry type mapping event names to their data types
export type EventRegistry = {
  [EVENT_NAMES.CP_INITIATED_ORDER]: CpInitiatedOrderData;
  [EVENT_NAMES.CP_SUBMITTED_PAYMENT]: SubmittedPaymentData;
  [EVENT_NAMES.CP_SELECTED_PACKAGE]: SelectedPackageData;
  [EVENT_NAMES.CP_NEW_PACKAGE]: NewPackageData;
  [EVENT_NAMES.CP_BUNDLED_DEAL]: BundledDealData;
  [EVENT_NAMES.CP_CHECK_TYPE]: CheckTypeData;
  [EVENT_NAMES.CP_UPLOAD_LOGO]: UploadLogoData;
  [EVENT_NAMES.CP_UPLOAD_ICON]: UploadIconData;
  [EVENT_NAMES.CP_ADDED_INTEGRATION]: AddedIntegrationData;
  [EVENT_NAMES.CP_SETUP_RESCREEN]: SetupRescreenData;
};

// Helper types
export type EventName = keyof EventRegistry;
export type EventData<K extends EventName> = EventRegistry[K];
